//Library Imports
import { Key, ReactNode } from "react";

// Component Exports
export interface IAutomationSate {
  emails: IEmails;
  modals: {
    addAction: {
      visible: boolean;
      id: string;
      tab: ControlName | "";
      from: string;
    };
    shareProfileConfiguration: boolean;
    shareProfileTemplatesData: ITemplateConfigurationData;
    deleteAction: boolean;
    deleteCondition: boolean;
    conditionConstraint: boolean;
    publish: boolean;
    inactive: boolean;
  };
  events: IEvents;
  controls: Array<IControls>;
  workflow: IWorkFlow;
  lists: ILists;
  userWorkflows: IUserWorkflows;
  deleteWorkflow: IDeleteWorkFlow;
  updateWorkflow: IUpdateWorkFlow;
  success: {
    type: string;
    payload: {
      [key: string]: any;
    };
  };
  conditionAction: {
    conditionId: number;
    conditionEmailId: number;
  };
  emailsLinks: {
    data: { [key: string]: any };
    loading: boolean;
  };
  emailPreviews: {
    data: { [key: string]: any };
    selectedEmailId: number;
    selectedCampaignId: number;
    textFetchSuccess: boolean;
    loading: boolean;
  };
}

export interface IWorkFlow {
  loading: boolean;
  error: boolean;
  flow: IFlow; // complete workflow
  defaultFlowKeys: number; // it help to fix the error while generating the node Id.
  // so when user reload the workflow page the 'react-id-generator' reset the counter which cause the problem
  // for example use add two node haveing ids 0 & 1 and saev it. If user reload the page the 'react-id-generator'
  // returns 1 as the counter reset. Now we use this state and add this number in the generated Id
  detail: {
    id: number;
    name: string;
    nameLoading: boolean;
    nameError: boolean;
    triggerId: number;
    triggerName: string;
    emailId: number; // email id which is associated with the trigger.
    emailName: string; // email name which is associated with the trigger.
    campaignId: number; // campaign id which is associated with the email.
    status: WFStatus;
    shareProfileUuid: string;
    errorNodes: string[];
    newlyAddedNodes: string[];
    triggerEmailSent: boolean;
    versionEmailType?: number;
    designNotReady?: boolean;
  };
  isRecentlyPublished: boolean; // this state use to show the confirm dialog modal so that user will redirect to the event email publish page.
  // use the following state to save the data of the node that user want to delete.
  // we have to use it because we're first showing a confirmation dialog/modal before deleting the node.
  toBeDeleteNode: {
    id: string;
    index?: number;
  };
  dropArea: {
    [key: string]: any;
  };
  copiedNode: {
    [key: string]: any;
  };
  // use the following state to so that action email should not be showing again on the trigger email popup and
  // action emails once added in the workflow.
  addEmails: number[];
  removeEmails: number[];
  shareProfileConfigurations: IShareProfileConfigurations; // client side to store configurations
  shareProfileConfigurationsData: any; // configurations data received from api as response
  sharedProfileConfigLoading: boolean;
  senderEmails: any[];
}

export interface IFlow {
  [key: string]: {
    childNodes: Array<string>;
    detail: {
      [key: string]: any;
    };
  };
}

export interface ITemplateConfigurationData {
  [key: string]: {
    title: string;
    template_configurations: {
      [key: string]: any;
    };
  };
}

// workflow status
export enum WFStatusEnum {
  DRAFT = "draft",
  INACTIVE = "inactive",
  ACTIVE = "active",
}

export type WFStatus = WFStatusEnum.DRAFT | WFStatusEnum.INACTIVE | WFStatusEnum.ACTIVE;

export interface IEvents {
  loading: boolean;
  error: boolean;
  searchTerm: string;
  data: IEventData[];
  searchedData: IEventData[];
}

export interface IEventData {
  id: number;
  title: string;
  type: string;
  info: string;
  iconType: string;
}

export interface IEventDataAction {
  id: number;
  name: string;
  type: string;
  title: string;
  description: string;
}

export interface IEmailDataAction {
  campaign_id: number;
  created_on: string;
  curated_articles_count: string;
  email_id: number;
  email_type: number;
  design_ready: boolean;
  last_updated: string;
  email_name: string;
  sender_name: string;
  sent_from: string;
  status: string;
  subject: string;
  thumbnail: string[];
  total_contacts: number | null;
  campaign_name: string;
}

export interface IAutomationAction {
  payload: any;
  type: string;
  events: IEventDataAction[];
  emails: IEmailDataAction[];
  selectedEvent: IEventData;
  selectedEventId: number;
  selectedEmail: IEmailData;
  selectedEmailId: number;
  selectedCampaignId: number;
  lists: IListData[];
  selectedLists: IListData[];
  isSearched?: boolean;
  userWorkflows: IUserWorkflowData[];
  totalCount: number;
  paginatedCall: boolean;
  shareProfileTemplates: IShareProfileTemplate[];
  senderEmails: ISenderEmail[];
  shareProfileConfigurations: IShareProfileConfigurations;
  newlyCreatedListInWorkflow: IListData;
}

export interface IEmails {
  loading: boolean;
  error: boolean;
  updateSuccess: boolean;
  searchTerm: string;
  filters: string;
  showUpdate: boolean;
  data: IEmailData[];
  totalCount: number;
  page: number;
  shareProfileTemplates: IShareProfileTemplate[];
  senderEmails: ISenderEmail[];
  selectedEmail: {
    // a state to retain the selected email if user redirects to edit email.
    id: number;
    title: string;
    campaignId: number;
  };
  form: {
    // data for each email for Update button
    senderName: string;
    sendFrom: string;
    emailSubject: string;
    emailId: number;
    designReady: boolean;
    versionEmailType: number;
    campaignId: number;
    errors: any;
    currentData: {
      senderName: string;
      sendFrom: string;
      emailSubject: string;
    };
  };
}

export interface IEmailData {
  campaignId: number;
  campaignName: string;
  emailId: number;
  versionEmailType: number;
  designReady: boolean;
  title: string;
  subject: string;
  senderName: string;
  sendFrom: string;
  date: string;
  thumbnail: string;
  sp_uuid?: string;
}

export interface ISenderEmail {
  domain: string;
  active: boolean;
  verified: boolean;
  id: number;
}

export type ConditionEmails = Pick<IEmailData, "emailId" | "title" | "sp_uuid">;

export interface IControls {
  id: number;
  name: ControlName;
  type: ControlType;
  title: string;
  conditions?: IControls[];
}

export enum ControlTypeEnum {
  ACTION = "ACTION",
  CONDITION = "CONDITION",
}

export type ControlType = ControlTypeEnum.ACTION | ControlTypeEnum.CONDITION;

export enum ControlNameEnum {
  SEND_EMAIL = "send email",
  ADD_LIST = "add to list",
  IF_ELSE = "If / Else",
  WAIT = "delay",
  END = "end",
}

export enum ConditionEnum {
  HAS_OPENED = "has opened",
  HAS_CLICKED = "has clicked",
  SEQUENCE = "email sequence",
}

export type ControlName =
  | ControlNameEnum.SEND_EMAIL
  | ControlNameEnum.ADD_LIST
  | ControlNameEnum.IF_ELSE
  // | ControlNameEnum.HAS_OPENED
  // | ControlNameEnum.HAS_CLICKED
  | ControlNameEnum.WAIT
  | ControlNameEnum.END;

export const enum NodeSide {
  RIGHT = "right",
  LEFT = "left",
}

export interface ILists {
  loading: boolean;
  error: boolean;
  searchTerm: string;
  data: IListData[];
  searchedData: IListData[];
  selectedLists: IListData[];
  totalCount: number;
  selectedListIds: number[];
  newListCreatedSuccess: boolean;
  newListCreatedError: boolean;
}

export interface IListData {
  list_id: Key | null | undefined;
  list_name: ReactNode;
  id: number;
  name: string;
}

export interface IUserWorkflows {
  loading: boolean;
  error: boolean;
  message: string;
  success: boolean;
  data: IUserWorkflowData[];
  searchTerm: string;
  filterType: string;
  searchedData: IUserWorkflowData[];
  page: number;
  totalCount: number;
}

export interface IUserWorkflowData {
  id: number;
  email_id: number;
  name: string;
  status: string;
  created_on: string;
}

export interface IDeleteWorkFlow {
  loading: boolean;
  error: boolean;
}

export interface IUpdateWorkFlow {
  loading: boolean;
  error: boolean;
}

export interface ParamsObjectType {
  [key: string]: any;
}

export interface IShareProfileTemplate {
  id: number;
  name: string;
}

export interface ISelectedTemplate {
  value: string | number;
  label: string | number;
}
export interface IShareProfileConfigurations {
  selectedTemplate: ISelectedTemplate;
  selectedConfigurations: ISelectedTemplate;
  selectedPersonalityMetrics: string[];
  senderName: string;
  sendFrom: string;
  emailSubject: string;
  replyTo: string;
  initials: string;
  signature: IShareProfileSignature | string;
  sendPlatform: number;
}

export interface IShareProfileSignature {
  id: number;
  title: string;
}

export interface IShareProfileConfigurations {}

// export interface obj {
//   childNodes: string[];
//   detail: {
//     nodeId: number;
//     nodeTitle: string;
//     conditionTime: string;
//     error: boolean;
//     linkType: string;
//     conditions: IIfElseCondition[];
//   };
// }

// export interface IIfElseCondition {
//   controlName: string;
//   emailId: number;
//   emailType: string;
//   emailName: string;
//   link: string;
//   title: string;
//   linkType: string;
//   sp_uuid: number;
// }
